import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { resolveAssetUrl } from '../api/client';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'vi_super_admin_tenant_view';

const TenantViewContext = createContext(null);

function readStoredTenant() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function normalizeTenant(account) {
  if (!account?.id) return null;
  return {
    id: account.id,
    name: account.name,
    logoUrl: account.logoUrl ? resolveAssetUrl(account.logoUrl) : null,
    primaryColor: account.primaryColor || null,
  };
}

export function TenantViewProvider({ children }) {
  const { isSuperAdmin } = useAuth();
  const [viewingTenant, setViewingTenant] = useState(() => readStoredTenant());

  const enterTenantView = useCallback(
    (account) => {
      if (!isSuperAdmin) return;
      const next = normalizeTenant(account);
      if (!next) return;
      setViewingTenant(next);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    },
    [isSuperAdmin]
  );

  const exitTenantView = useCallback(() => {
    setViewingTenant(null);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  useEffect(() => {
    if (!isSuperAdmin && viewingTenant) {
      setViewingTenant(null);
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [isSuperAdmin, viewingTenant]);

  const value = useMemo(
    () => ({
      viewingTenant: isSuperAdmin ? viewingTenant : null,
      isViewingTenant: Boolean(isSuperAdmin && viewingTenant),
      enterTenantView,
      exitTenantView,
    }),
    [isSuperAdmin, viewingTenant, enterTenantView, exitTenantView]
  );

  return <TenantViewContext.Provider value={value}>{children}</TenantViewContext.Provider>;
}

export function useTenantView() {
  const context = useContext(TenantViewContext);
  if (!context) {
    throw new Error('useTenantView must be used within TenantViewProvider');
  }
  return context;
}
